import { type FormEvent, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button, ErrorBanner, Logo } from "../components/ui";
import { useAuth } from "../state/auth";

export function AuthShell({ title, subtitle, children }: { title: string; subtitle: string; children: React.ReactNode }) {
    return (
        <div className="flex min-h-screen items-center justify-center bg-surface-sunken px-4 py-12">
            <div className="w-full max-w-sm space-y-6">
                <div className="space-y-1.5 text-center">
                    <Logo className="mx-auto size-11" />
                    <h1 className="text-xl font-semibold text-ink">{title}</h1>
                    <p className="text-sm text-ink-soft">{subtitle}</p>
                </div>
                <div className="rounded-2xl border border-line bg-surface p-6">{children}</div>
            </div>
        </div>
    );
}

export function Field({
    label,
    ...rest
}: { label: string } & React.InputHTMLAttributes<HTMLInputElement>) {
    return (
        <label className="block space-y-1.5">
            <span className="text-sm font-medium text-ink">{label}</span>
            <input
                {...rest}
                className="w-full rounded-xl border border-line bg-surface px-3.5 py-2.5 text-sm text-ink transition-colors placeholder:text-ink-soft/60 focus:border-brand focus:outline-2 focus:outline-offset-1 focus:outline-brand"
            />
        </label>
    );
}

export function LoginPage() {
    const { signIn } = useAuth();
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);

    async function handleSubmit(event: FormEvent) {
        event.preventDefault();
        setError(null);
        setBusy(true);
        try {
            await signIn(email, password);
            navigate("/", { replace: true });
        } catch (cause) {
            setError(cause instanceof Error ? cause.message : "登录失败，请稍后重试");
        } finally {
            setBusy(false);
        }
    }

    return (
        <AuthShell title="登录" subtitle="继续你的入籍考试复习">
            <form onSubmit={handleSubmit} className="space-y-4">
                {error ? <ErrorBanner message={error} /> : null}

                <Field
                    label="邮箱"
                    type="email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    autoComplete="email"
                    required
                    placeholder="you@example.com"
                />
                <div className="space-y-1.5">
                    <Field
                        label="密码"
                        type="password"
                        value={password}
                        onChange={(event) => setPassword(event.target.value)}
                        autoComplete="current-password"
                        required
                    />
                    <div className="text-right">
                        <Link to="/forgot-password" className="text-xs text-ink-soft hover:text-brand hover:underline">
                            忘记密码？
                        </Link>
                    </div>
                </div>

                <Button type="submit" loading={busy} className="w-full">
                    登录
                </Button>

                <p className="text-center text-sm text-ink-soft">
                    还没有账号？{" "}
                    <Link to="/register" className="font-medium text-brand hover:underline">
                        注册一个
                    </Link>
                </p>
            </form>
        </AuthShell>
    );
}
